import {
  ANNUAL_OPEN_ENROLLMENT_LABELS,
  enumLabel,
  formatEligibilityHours,
  formatRateGuarantee,
  isSentinel,
  sentinelLabel,
} from "./shared";
import {
  copayAllowance,
  copayTyped,
  formatConventionalContact,
  formatContactLensElective,
  formatContactLensExam,
  formatContactLensMedNec,
  formatDisposableContact,
  formatFramesAfterCopay,
  formatProgressiveLenses,
  lensFullAllowance,
  VisionFieldFormatter,
} from "./visionUtils";

const FREQUENCY_LABELS: Record<string, string> = {
  twelve: "12",
  twentyFour: "24",
  calendarYear: "Calendar Year",
  everyOtherCalendarYear: "Every Other Calendar Year",
  notIncluded: "Not Included",
  notDisclosed: "Not Disclosed",
};

/**
 * Exam/Lenses/Frames frequency values, joined into the single row the
 * Quotes tab displays (the three underlying rows are collapsed into
 * `examFrequency` by `collapseAmountRows`).
 */
const frequencyValueLabel = (value: unknown): string => {
  if (isSentinel(value)) return sentinelLabel(value as string);
  return typeof value === "string" ? (FREQUENCY_LABELS[value] ?? "") : "";
};

const formatFrequencyGroup: VisionFieldFormatter = (row) =>
  [
    frequencyValueLabel(row["examFrequency"]),
    frequencyValueLabel(row["lensesFrequency"]),
    frequencyValueLabel(row["framesFrequency"]),
  ].join(" / ");

const EMPLOYER_CONTRIBUTION_LABELS: Record<string, string> = {
  oneHundredPercentEmployerPaid: "100% Employer Paid",
  contributory: "Contributory",
  voluntary: "Voluntary",
  bonusUp: "Bonus Up",
  grossUp: "Gross Up",
  // Vision's mapping rules render the notIncluded contribution value as
  // "Not Disclosed" rather than the default sentinel label.
  notIncluded: "Not Disclosed",
};

// notIncluded is a sentinel, so the generic enumLabel would resolve it to
// "Not Included" before consulting the map above. Employer Contribution needs
// the map to win, so look it up first and only then fall back to the sentinel.
const formatEmployerContribution: VisionFieldFormatter = (row) => {
  const value = row["employerContribution"];
  if (typeof value !== "string" || value === "") return "";
  return (
    EMPLOYER_CONTRIBUTION_LABELS[value] ??
    (isSentinel(value) ? sentinelLabel(value) : "")
  );
};

// The carrier's own network name ("VSP Choice"), free text rather than an
// enum — shown as authored, trimmed because it's hand-entered.
const formatNetworkName: VisionFieldFormatter = (row) => {
  const value = row["networkName"];
  return typeof value === "string" ? value.trim() : "";
};

export const visionFormatters: Record<string, VisionFieldFormatter> = {
  networkName: formatNetworkName,
  examCopay: copayTyped("examCopayType", "examCopayAmount"),
  materialsCopay: copayTyped("materialsCopayType", "materialsCopayAmount"),

  singleLenses: lensFullAllowance(
    "singleLensesType",
    "singleLensesCopayAmount",
    "singleLensesAllowanceAmount",
  ),
  bifocalLenses: lensFullAllowance(
    "bifocalLensesType",
    "bifocalLensesCopayAmount",
    "bifocalLensesAllowanceAmount",
  ),
  trifocalLenses: lensFullAllowance(
    "trifocalLensesType",
    "trifocalLensesCopayAmount",
    "trifocalLensesAllowanceAmount",
  ),
  lenticularLenses: lensFullAllowance(
    "lenticularLensesType",
    "lenticularLensesCopayAmount",
    "lenticularLensesAllowanceAmount",
  ),
  standardProgressive: copayAllowance(
    "standardProgressiveType",
    "standardProgressiveCopayAmount",
    "standardProgressiveAllowanceAmount",
  ),
  premiumProgressive: copayAllowance(
    "premiumProgressiveType",
    "premiumProgressiveCopayAmount",
    "premiumProgressiveAllowanceAmount",
  ),
  standardFrames: formatFramesAfterCopay,
  conventionalContact: formatConventionalContact,
  disposableContact: formatDisposableContact,
  contactLensMedNec: formatContactLensMedNec,
  progressiveLenses: formatProgressiveLenses,
  contactLensElective: formatContactLensElective,
  contactLensExam: formatContactLensExam,
  examFrequency: formatFrequencyGroup,
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  employerContribution: formatEmployerContribution,
  eligibilityHours: formatEligibilityHours,
  rateGuarantee: formatRateGuarantee,
};

export const formatVisionField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = visionFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
